import type { UIMessage } from 'ai';
import type { ClassifierResult } from '@flintmint/core-shared/medical-note';

import { getCurrentTurnAssistantMessages, isEditorWriteToolType } from '@flintmint/core-shared/utils/editor-write-tool';
import {
  getMessageMedicalClassification,
  getToolParts,
  type ToolStreamingPart,
} from '@flintmint/web-shared/utils/llm-message-utils';

type CurrentTurnMedicalClassificationMatch = {
  message: UIMessage;
  classification: ClassifierResult;
};

export function getLatestCurrentTurnMedicalClassificationMatch(
  messages: UIMessage[]
): CurrentTurnMedicalClassificationMatch | null {
  const assistantMessages = getCurrentTurnAssistantMessages(messages);
  for (let i = assistantMessages.length - 1; i >= 0; i -= 1) {
    const message = assistantMessages[i];
    const classification = getMessageMedicalClassification(message);
    if (classification) {
      return { message, classification };
    }
  }

  return null;
}

export function getLatestCurrentTurnMedicalClassification(messages: UIMessage[]): ClassifierResult | null {
  return getLatestCurrentTurnMedicalClassificationMatch(messages)?.classification ?? null;
}

export function hasSetDocumentNameInCurrentTurn(messages: UIMessage[]): boolean {
  return getCurrentTurnAssistantMessages(messages).some((message) =>
    getToolParts(message).some((part) => isSuccessfulSetDocumentNameToolPart(part))
  );
}

function isSuccessfulSetDocumentNameToolPart(part: ToolStreamingPart): boolean {
  if (part.type !== 'tool-setDocumentName' || part.state !== 'output-available') return false;

  const output = part.output;
  if (!output || typeof output !== 'object') return false;

  return (output as { applied?: unknown }).applied === true;
}

const REFUSED_EDITOR_WRITE_OUTPUT = {
  success: false,
  error: 'Edit not applied: the document would exceed the size limit.',
};

export function getCurrentTurnEditorWriteToolCallIds(messages: UIMessage[]): string[] {
  return getCurrentTurnAssistantMessages(messages)
    .flatMap(getToolParts)
    .filter((part) => isEditorWriteToolType(part.type))
    .map((part) => part.toolCallId);
}

// Rewrites the outputs of the current turn's editor writes, except `keptToolCallIds`, as not
// applied, so the history later requests send matches the document a size refusal restored.
export function markEditorWritesRefused(messages: UIMessage[], keptToolCallIds: string[]): UIMessage[] {
  const turnMessages = new Set(getCurrentTurnAssistantMessages(messages));

  return messages.map((message) =>
    turnMessages.has(message)
      ? {
          ...message,
          parts: message.parts.map((part) =>
            isRefusedEditorWritePart(part, keptToolCallIds)
              ? ({ ...part, output: REFUSED_EDITOR_WRITE_OUTPUT } as UIMessage['parts'][number])
              : part
          ),
        }
      : message
  );
}

function isRefusedEditorWritePart(part: UIMessage['parts'][number], keptToolCallIds: string[]): boolean {
  const toolPart = part as Partial<ToolStreamingPart>;
  return (
    typeof toolPart.type === 'string' &&
    isEditorWriteToolType(toolPart.type) &&
    toolPart.state === 'output-available' &&
    !keptToolCallIds.includes(toolPart.toolCallId ?? '')
  );
}
